import {
  createBrowserRouter,
  createMemoryRouter,
  Navigate,
  type RouteObject,
} from 'react-router-dom'

import { RequireSession } from '../../features/identity'
import { PageLoader } from '../../shared/ui/PageLoader'
import { RootLayout } from './RootLayout'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { profilePathPrefixes, publicPaths } from './route-paths'
import { storeRoutes } from './store-routes'
import { cardContentEditorRoutes } from './card-content-editor-routes'
import { compactPublicPages } from './compact-public-paths'
import { resolveStoryNavigation } from './story-display-policy'
import { reviewHistoryRoutes } from './review-history-routes'

const flashcardsHandle = { studyMode: 'flashcards' } as const
const immersiveLearningHandle = { immersiveLearning: true } as const

const contentRoutes: RouteObject[] = [
  ...cardContentEditorRoutes,
  {
    path: `${profilePathPrefixes.cardSets.slice(1)}/create`,
    lazy: () => import('../../features/content/ui/CardSetEditorRoute'),
  },
  {
    path: `${profilePathPrefixes.cardSets.slice(1)}/:cardSetId/edit`,
    lazy: () => import('../../features/content/ui/CardSetEditorRoute'),
  },
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.cardSets.slice(1)}/:cardSetId`,
    lazy: () => import('../../features/learning/ui/PersonalLearningRoute'),
  },
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.cardSets.slice(1)}/:cardSetId/studied`,
    lazy: () => import('../../features/learning/ui/PersonalLearningRoute'),
  },
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.cardSets.slice(1)}/:cardSetId/difficult`,
    lazy: () => import('../../features/learning/ui/PersonalLearningRoute'),
  },
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.user.slice(1)}/:userId/card-sets/:cardSetId`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  },
  ...['/studied', '/difficult'].map((suffix): RouteObject => ({
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.user.slice(1)}/:userId/card-sets/:cardSetId${suffix}`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  })),
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.search.slice(1)}/card-sets/:cardSetId`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  },
  ...['/studied', '/difficult'].map((suffix): RouteObject => ({
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.search.slice(1)}/card-sets/:cardSetId${suffix}`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  })),
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.favoriteCardSets.slice(1)}/:cardSetId`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  },
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.favoriteCardSets.slice(1)}/:cardSetId/studied`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  },
  {
    handle: flashcardsHandle,
    path: `${profilePathPrefixes.favoriteCardSets.slice(1)}/:cardSetId/difficult`,
    lazy: () => import('../../features/learning/ui/LearningRoute'),
  },
  {
    path: `${profilePathPrefixes.folders.slice(1)}/create`,
    lazy: () => import('../../features/content/ui/FolderEditorRoute'),
  },
  {
    path: `${profilePathPrefixes.folders.slice(1)}/:folderId/edit`,
    lazy: () => import('../../features/content/ui/FolderEditorRoute'),
  },
  {
    path: `${profilePathPrefixes.folders.slice(1)}/:folderId/card-sets/create`,
    lazy: () => import('../../features/content/ui/CardSetEditorRoute'),
  },
  {
    path: profilePathPrefixes.cardSets.slice(1),
    handle: { storyAutoDisplay: 'allow' },
    lazy: () => import('../../features/content/ui/CardSetsRoute'),
  },
  {
    path: profilePathPrefixes.favoriteCardSets.slice(1),
    handle: { storyAutoDisplay: 'allow' },
    lazy: () => import('../../features/content/ui/FavoriteCardSetsRoute'),
  },
  {
    path: profilePathPrefixes.folders.slice(1),
    handle: { storyAutoDisplay: 'allow' },
    lazy: () => import('../../features/content/ui/FoldersRoute'),
  },
  {
    path: profilePathPrefixes.favoriteFolders.slice(1),
    handle: { storyAutoDisplay: 'allow' },
    lazy: () => import('../../features/content/ui/FavoriteFoldersRoute'),
  },
  {
    path: `${profilePathPrefixes.favoriteFolders.slice(1)}/:folderId`,
    lazy: () => import('../../features/content/ui/FolderRoute'),
  },
  {
    path: `${profilePathPrefixes.folders.slice(1)}/:folderId`,
    lazy: () => import('../../features/content/ui/FolderRoute'),
  },
]

const groupRoutes: RouteObject[] = [
  {
    path: `${profilePathPrefixes.groups.slice(1)}/create`,
    lazy: () => import('../../features/groups/ui/GroupEditorRoute'),
  },
  {
    path: `${profilePathPrefixes.invite.slice(1)}/:groupId/:token`,
    lazy: () => import('../../features/groups/ui/GroupInviteRoute'),
  },
  {
    path: profilePathPrefixes.groups.slice(1),
    handle: { storyAutoDisplay: 'allow' },
    lazy: () => import('../../features/groups/ui/GroupsRoute'),
  },
  {
    path: profilePathPrefixes.participatingGroups.slice(1),
    handle: { storyAutoDisplay: 'allow' },
    lazy: () => import('../../features/groups/ui/GroupsRoute'),
  },
  ...[
    profilePathPrefixes.groups,
    profilePathPrefixes.participatingGroups,
  ].flatMap((prefix): RouteObject[] => [
    {
      path: `${prefix.slice(1)}/:groupId`,
      lazy: () => import('../../features/groups/ui/GroupRoute'),
    },
    {
      path: `${prefix.slice(1)}/:groupId/card-sets`,
      lazy: () => import('../../features/groups/ui/GroupRoute'),
    },
    {
      path: `${prefix.slice(1)}/:groupId/members`,
      lazy: () => import('../../features/groups/ui/GroupRoute'),
    },
    {
      path: `${prefix.slice(1)}/:groupId/stats`,
      lazy: () => import('../../features/groups/ui/GroupRoute'),
    },
    {
      path: `${prefix.slice(1)}/:groupId/edit`,
      lazy: () => import('../../features/groups/ui/GroupEditorRoute'),
    },
    {
      path: `${prefix.slice(1)}/:groupId/invite`,
      lazy: () => import('../../features/groups/ui/GroupInviteRoute'),
    },
    ...['', '/studied', '/difficult'].map((suffix): RouteObject => ({
      handle: flashcardsHandle,
      path: `${prefix.slice(1)}/:groupId/card-sets/:cardSetId${suffix}`,
      lazy: () => import('../../features/learning/ui/LearningRoute'),
    })),
  ]),
]

const billingRoutes: RouteObject[] = [
  {
    path: profilePathPrefixes.partner.slice(1),
    lazy: () => import('../../features/billing/ui/PartnerContractsRoute'),
  },
  {
    path: `${profilePathPrefixes.partner.slice(1)}/:contractId`,
    lazy: () => import('../../features/billing/ui/PartnerContractRoute'),
  },
  {
    path: profilePathPrefixes.subscription.slice(1),
    lazy: () => import('../../features/billing/ui/SubscriptionRoute'),
  },
  {
    path: `${profilePathPrefixes.subscription.slice(1)}/tariffs`,
    lazy: () => import('../../features/billing/ui/SelectTariffRoute'),
  },
  {
    path: profilePathPrefixes.orderList.slice(1),
    lazy: () => import('../../features/billing/ui/OrdersRoute'),
  },
  {
    path: profilePathPrefixes.referrals.slice(1),
    lazy: () => import('../../features/referrals/ui/ReferralActivityRoute'),
  },
]

const profileRoutes: RouteObject[] = [
  {
    path: profilePathPrefixes.aiTools.slice(1),
    lazy: () => import('../../features/ai-tools/ui/AiToolsRoute'),
  },
  {
    path: profilePathPrefixes.productAutomationAdmin.slice(1),
    lazy: () =>
      import('../../features/product-automation/ui/ProductAutomationAdminRoute'),
  },
  {
    path: profilePathPrefixes.knowledgeMap.slice(1),
    lazy: () => import('../../features/knowledge-map/ui/KnowledgeMapRoute'),
  },
  {
    path: profilePathPrefixes.settings.slice(1),
    lazy: () => import('../../features/identity/ui/SettingsRoute'),
  },
  {
    path: `${profilePathPrefixes.user.slice(1)}/:userId`,
    lazy: () => import('../../features/content/ui/UserLibraryRoute'),
  },
  {
    path: `${profilePathPrefixes.user.slice(1)}/:userId/card-sets`,
    lazy: () => import('../../features/content/ui/UserLibraryRoute'),
  },
  {
    path: `${profilePathPrefixes.user.slice(1)}/:userId/folders`,
    lazy: () => import('../../features/content/ui/UserLibraryRoute'),
  },
  {
    path: `${profilePathPrefixes.user.slice(1)}/:userId/folders/:folderId`,
    lazy: () => import('../../features/content/ui/FolderRoute'),
  },
  {
    path: profilePathPrefixes.search.slice(1),
    lazy: () => import('../../features/content/ui/SearchRoute'),
  },
  {
    path: `${profilePathPrefixes.search.slice(1)}/folders`,
    lazy: () => import('../../features/content/ui/SearchRoute'),
  },
  {
    path: `${profilePathPrefixes.search.slice(1)}/folders/:folderId`,
    lazy: () => import('../../features/content/ui/FolderRoute'),
  },
]

const spacedRepetitionRoutes: RouteObject[] = [
  {
    element: <Navigate replace to={publicPaths.home} />,
    path: profilePathPrefixes.repetition.slice(1),
  },
  {
    path: profilePathPrefixes.scheduledRepetition.slice(1),
    lazy: () => import('../../features/dashboard/ui/ScheduledReviewsRoute'),
  },
  {
    handle: immersiveLearningHandle,
    path: `${profilePathPrefixes.repetition.slice(1)}/session`,
    lazy: () =>
      import('../../features/spaced-repetition/ui/ReviewSessionRoute'),
  },
  {
    handle: immersiveLearningHandle,
    path: `${profilePathPrefixes.repetition.slice(1)}/card-groups/:cardGroupId/session`,
    lazy: () =>
      import('../../features/spaced-repetition/ui/ReviewSessionRoute'),
  },
]

export const appRoutes: RouteObject[] = [
  {
    path: publicPaths.home,
    element: (
      <RootLayout
        storyNavigationType={(path) => resolveStoryNavigation(appRoutes, path)}
      />
    ),
    hydrateFallbackElement: <PageLoader />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        handle: { storyAutoDisplay: 'allow' },
        lazy: () => import('./HomeRoute'),
      },
      {
        lazy: async () => {
          const { PublicLayout } = await import('../shell/PublicLayout')
          return { Component: PublicLayout }
        },
        children: [
          {
            path: publicPaths.login.slice(1),
            lazy: () => import('../../features/identity/ui/AuthRoute'),
          },
          {
            path: `${publicPaths.resetPassword.slice(1)}/*`,
            lazy: () => import('../../features/identity/ui/PasswordResetRoute'),
          },
          {
            path: publicPaths.confirm.slice(1),
            lazy: () => import('../../features/identity/ui/ConfirmRoute'),
          },
          {
            path: publicPaths.tariffs.slice(1),
            lazy: () => import('../../features/billing/ui/PublicTariffsRoute'),
          },
          {
            path: 'r/:code',
            lazy: () =>
              import('../../features/referrals/ui/ReferralLandingRoute'),
          },
          {
            path: publicPaths.price.slice(1),
            lazy: () => import('../../features/billing/ui/PublicTariffsRoute'),
          },
          {
            path: publicPaths.agreements.slice(1),
            lazy: () => import('../../features/public-pages/ui/TermsRoute'),
          },
          {
            path: publicPaths.storeOffer.slice(1),
            lazy: () =>
              import('../../features/public-pages/ui/StoreOfferRoute'),
          },
          {
            path: publicPaths.iosPwa.slice(1),
            lazy: () =>
              import('../../features/public-pages/ui/IosInstallRoute'),
          },
          {
            path: publicPaths.error.slice(1),
            lazy: () =>
              import('../../features/public-pages/ui/PublicErrorRoute'),
          },
          ...compactPublicPages.map((path) => ({
            path: path.slice(1),
            lazy: () =>
              import('../../features/public-pages/ui/InformationRoute'),
          })),
        ],
      },
      {
        element: <RequireSession loginPath={publicPaths.login} />,
        handle: { personalArea: true },
        children: [
          {
            lazy: async () => {
              const { ProfileLayout } = await import('../shell/ProfileLayout')
              return { Component: ProfileLayout }
            },
            children: [
              { path: 'guide', lazy: () => import('./GuideRoute') },
              { path: 'help', lazy: () => import('./GuideLegacyRoute') },
              { path: 'help/:slug', lazy: () => import('./GuideLegacyRoute') },
              {
                path: 'recommendations',
                lazy: () =>
                  import('../../features/engagement/ui/RecommendationsRoute'),
              },
              ...contentRoutes,
              ...groupRoutes,
              ...billingRoutes,
              ...storeRoutes,
              ...profileRoutes,
              ...reviewHistoryRoutes,
              ...spacedRepetitionRoutes,
            ],
          },
        ],
      },
      {
        path: '*',
        lazy: () => import('../../shared/ui/NotFoundRoute'),
      },
    ],
  },
]

export function createAppBrowserRouter() {
  return createBrowserRouter(appRoutes)
}

export function createAppMemoryRouter(initialEntries: string[] = ['/']) {
  return createMemoryRouter(appRoutes, { initialEntries })
}

export const appRouter = createAppBrowserRouter()
